import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, RotateCcw, Sparkles } from "lucide-react";
import { GOOGLE_FORM_URL, PERSONAS } from "@/data/content";
import { FadeUp, Overline } from "./Reveal";

const QUESTIONS = [
  {
    q: "What brings you to Africa?",
    options: [
      { t: "Grow my money — property and business opportunities", p: "investor" },
      { t: "Sun, events and pure enjoyment", p: "tourist" },
      { t: "Coming home for good", p: "returnee" },
      { t: "A work posting or a whole new chapter", p: "expat" },
    ],
  },
  {
    q: "How long are you staying?",
    options: [
      { t: "However long the deal takes — I'll be back and forth", p: "investor" },
      { t: "A week or three, beautifully planned", p: "tourist" },
      { t: "I'm moving — this is permanent", p: "returnee" },
      { t: "A year or more on contract", p: "expat" },
    ],
  },
  {
    q: "What's your biggest worry?",
    options: [
      { t: "Fake deals — I need due diligence I can trust", p: "investor" },
      { t: "Tourist traps and plans falling apart at midnight", p: "tourist" },
      { t: "Housing, schools and the first ninety days", p: "returnee" },
      { t: "Daily-life setup in unfamiliar systems", p: "expat" },
    ],
  },
  {
    q: "Pick your ideal first day:",
    options: [
      { t: "Site tours and meetings with vetted lawyers and brokers", p: "investor" },
      { t: "Beach club by day, rooftop dinner by night", p: "tourist" },
      { t: "Keys to my own place and a family welcome", p: "returnee" },
      { t: "Airport pickup, SIM and driver already sorted", p: "expat" },
    ],
  },
];

const ORDER = ["investor", "tourist", "returnee", "expat"];

export const JourneyQuiz = () => {
  const [step, setStep] = useState(-1); // -1 intro, 0..3 questions, 4 result
  const [scores, setScores] = useState({});

  const answer = (p) => {
    const next = { ...scores, [p]: (scores[p] || 0) + 1 };
    setScores(next);
    setStep(step + 1);
  };

  const restart = () => {
    setScores({});
    setStep(-1);
  };

  const winner = ORDER.reduce((best, p) => ((scores[p] || 0) > (scores[best] || 0) ? p : best), ORDER[0]);
  const persona = PERSONAS[winner];

  return (
    <section id="journey-quiz" data-testid="journey-quiz-section" className="bg-jawa-espresso py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
        <FadeUp>
          <Overline>Find your journey</Overline>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-jawa-ivory sm:text-4xl">
            Not sure where you fit? Sixty seconds. Four questions. Your journey.
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-jawa-ivory/70">
            Investor, Tourist, Returnee or Expat — answer four quick questions and we'll match you to the
            JÀWÀ journey built for you, with a bespoke itinerary one step away.
          </p>
          {step === -1 && (
            <button
              onClick={() => setStep(0)}
              data-testid="quiz-start-button"
              className="mt-8 inline-flex items-center gap-2 bg-jawa-coral px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-turquoise hover:text-jawa-nearblack"
            >
              <Sparkles className="h-4 w-4" /> Start the quiz
            </button>
          )}
        </FadeUp>

        <FadeUp delay={0.08}>
          <div className="min-h-[22rem] border border-jawa-ivory/15 bg-jawa-nearblack p-8 lg:p-10" data-testid="quiz-card">
            <AnimatePresence mode="wait">
              {step >= 0 && step < QUESTIONS.length && (
                <motion.div
                  key={`q-${step}`}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-gold" data-testid="quiz-progress">
                      Question {step + 1} of {QUESTIONS.length}
                    </p>
                    <div className="flex gap-1.5">
                      {QUESTIONS.map((_, i) => (
                        <span key={i} className={`h-1 w-6 ${i <= step ? "bg-jawa-gold" : "bg-jawa-ivory/20"}`} />
                      ))}
                    </div>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl font-medium text-jawa-ivory" data-testid="quiz-question-text">
                    {QUESTIONS[step].q}
                  </h3>
                  <div className="mt-6 space-y-3">
                    {QUESTIONS[step].options.map((o, oi) => (
                      <button
                        key={o.t}
                        onClick={() => answer(o.p)}
                        data-testid={`quiz-option-${step}-${oi}`}
                        className="group flex w-full items-center justify-between gap-3 border border-jawa-ivory/15 px-5 py-4 text-left text-sm text-jawa-ivory/85 transition-colors hover:border-jawa-turquoise hover:bg-jawa-turquoise/10 hover:text-jawa-ivory"
                      >
                        {o.t}
                        <ArrowRight className="h-4 w-4 shrink-0 text-jawa-turquoise opacity-0 transition-opacity group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === QUESTIONS.length && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="quiz-result"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-gold">Your journey</p>
                  <div className="mt-5 flex items-center gap-5">
                    <img
                      src={persona.image}
                      alt={persona.title}
                      data-testid="quiz-result-image"
                      className="h-20 w-20 shrink-0 border border-jawa-gold/40 object-cover"
                    />
                    <div>
                      <h3 className="font-serif text-3xl font-medium text-jawa-ivory" data-testid="quiz-result-name">
                        {persona.title}
                      </h3>
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-jawa-turquoise">
                        Like {persona.name} — {persona.meta}
                      </p>
                    </div>
                  </div>
                  <p className="mt-5 text-sm leading-relaxed text-jawa-ivory/70">{persona.strapline}</p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link
                      to={`/journeys/${winner}`}
                      data-testid="quiz-journey-link"
                      className="inline-flex items-center gap-2 bg-jawa-coral px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-ivory hover:text-jawa-espresso"
                    >
                      Explore my journey <ArrowRight className="h-4 w-4" />
                    </Link>
                    <a
                      href={GOOGLE_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quiz-bespoke-link"
                      className="inline-flex items-center gap-2 border border-jawa-turquoise px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:bg-jawa-turquoise hover:text-jawa-nearblack"
                    >
                      Get my bespoke itinerary <ArrowUpRight className="h-4 w-4" />
                    </a>
                    <button
                      onClick={restart}
                      data-testid="quiz-restart-button"
                      className="inline-flex items-center gap-2 px-4 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory/50 transition-colors hover:text-jawa-ivory"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Retake
                    </button>
                  </div>
                </motion.div>
              )}

              {step === -1 && (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-full min-h-[18rem] flex-col items-start justify-center"
                >
                  <p className="font-serif text-2xl italic leading-snug text-jawa-ivory/60">
                    "Answer honestly — your Lagos self already knows."
                  </p>
                  <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-jawa-ivory/40">
                    No sign-up · No email needed
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
